// pages/EpisodePlayer.jsx
// Course player — implements "Course Player.dc.html" from the "Video course
// purchase page" design project. Full-viewport shell: navy top bar with
// Previous / Complete-and-continue, a curriculum sidebar with progress, and the
// lesson area (video + exercises) fed by the live courses API.
//
// Progress wiring (unchanged from the previous player): while playing, POST
// lastPositionSeconds on a 15 second throttle. Once the viewer passes 90%
// watched, POST isCompleted true a single time (guarded by a ref so it never
// repeats). On mount we resume from lastPositionSeconds. Completing also
// refetches course progress so the sidebar dots and percent stay
// server-authoritative (sessionDone only paints dots optimistically).
//
// When the episode is locked, a locked panel replaces the player with an unlock
// CTA. Exercises still render below in preview mode; each exercise block figures
// out its own locked state from the absence of solution fields.

import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { coursesApi } from "../api/courses";
import { isLoggedIn, getUser, clearAuth } from "../api/auth";
import ExerciseBlock, { ExerciseLesson } from "../components/exercises/ExerciseBlocks";
import VideoPlayer from "../components/VideoPlayer";

const C = {
  bg: "#fafaf9",
  navy: "#1e1b4b",
  amber: "#d97706",
  blue: "#2563eb",
  blueDark: "#1e40af",
  blueBg: "#eff6ff",
  red: "#e11d48",
  ink: "#18181b",
  body: "#3f3f46",
  muted: "#71717a",
  soft: "#a1a1aa",
  line: "#e4e4e7",
  hover: "#f4f4f5",
  dot: "#d4d4d8",
  code: "#0d1117",
};

const mono = "'Space Mono', monospace";
const jmono = "'JetBrains Mono', monospace";
const serif = "'Fraunces', Georgia, serif";

const SAVE_THROTTLE_MS = 15000;
const COMPLETE_AT = 0.9;

function fmtDuration(sec) {
  if (!sec && sec !== 0) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const LockIcon = ({ size = 14, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Lock panel shared by the video and the exercise branches. Self contained: it
// carries its own dark surface instead of relying on the video wrapper, and it
// has no 16 / 9 aspect ratio, so it sits equally well on an exercise page. The
// body copy is a prop because each kind says something different.
const LockPanel = ({ body, onUnlock }) => (
  <div style={{ background: C.code, borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, position: "relative", padding: "72px 24px", textAlign: "center" }}>
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 40%, rgba(37,99,235,0.22), transparent 65%)" }} />
    <span style={{ position: "absolute", top: -60, left: -60, width: 240, height: 240, borderRadius: "50%", background: C.blue, opacity: 0.07 }} />
    <span style={{ position: "absolute", bottom: -80, right: -40, width: 300, height: 300, borderRadius: "50%", background: C.blue, opacity: 0.08 }} />
    <span style={{ position: "relative", width: 72, height: 72, borderRadius: "50%", background: "rgba(255,255,255,0.08)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <LockIcon size={26} />
    </span>
    <div style={{ position: "relative", fontFamily: serif, fontSize: 20, fontWeight: 700, color: "#fff" }}>
      This lesson is locked
    </div>
    <div style={{ position: "relative", fontFamily: mono, fontSize: 11, letterSpacing: "0.06em", color: "rgba(255,255,255,0.5)", maxWidth: 380, lineHeight: 1.7 }}>
      {body}
    </div>
    <button
      className="cpl-amber"
      style={{ position: "relative", marginTop: 4 }}
      onClick={onUnlock}
    >
      Unlock course →
    </button>
  </div>
);

function EpisodePlayer() {
  const { courseSlug, episodeSlug } = useParams();
  const navigate = useNavigate();

  // Same auth source the main site Navbar uses (localStorage-backed helpers).
  const loggedIn = isLoggedIn();
  const user = getUser();
  const firstName = user?.name?.split(" ")[0];

  const handleSignOut = () => {
    clearAuth();
    window.location.href = "/";
  };

  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Episodes completed this session, painted immediately while the server
  // progress refetch is in flight.
  const [sessionDone, setSessionDone] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  // Set when the player has given up recovering a failed video request.
  // Shape is { reason, code } as documented on VideoPlayer.
  const [playbackError, setPlaybackError] = useState(null);

  // Holds the Plyr instance (currentTime/duration/paused match the media
  // element API, so the progress handlers read it the same way).
  const playerRef = useRef(null);
  const mainRef = useRef(null);
  const lastSaveRef = useRef(0);
  const completedRef = useRef(false);
  const resumeAtRef = useRef(0);

  useEffect(() => {
    let active = true;
    Promise.all([
      coursesApi.getCourse(courseSlug),
      coursesApi.getProgress(courseSlug).catch(() => null),
    ]).then(([courseData, progressData]) => {
      if (!active) return;
      setCourse(courseData);
      setProgress(progressData);
    });
    return () => {
      active = false;
    };
  }, [courseSlug]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNavOpen(false);
    // Reset the per-episode progress guards for the new episode.
    lastSaveRef.current = 0;
    completedRef.current = false;
    resumeAtRef.current = 0;
    setPlaybackError(null);
    coursesApi
      .getEpisode(courseSlug, episodeSlug)
      .then((data) => {
        if (!active) return;
        setEpisode(data);
        resumeAtRef.current = data?.lastPositionSeconds || 0;
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    // "instant" because the app's global smooth scroll would animate this.
    mainRef.current?.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      active = false;
    };
  }, [courseSlug, episodeSlug]);

  function save(lastPositionSeconds, isCompleted) {
    // Anonymous viewers can watch free episodes; only saved sessions persist
    // progress (an anonymous POST would 401 and bounce them to /login).
    if (!localStorage.getItem("sc_token")) return Promise.resolve();
    return coursesApi
      .saveProgress(courseSlug, episodeSlug, { lastPositionSeconds, isCompleted })
      .catch(() => {});
  }

  function refreshProgress() {
    coursesApi
      .getProgress(courseSlug)
      .then(setProgress)
      .catch(() => {});
  }

  // Re-reads the episode purely to get a fresh video URL, for the player's
  // recovery path. Deliberately not coursesApi.getEpisode: apiClient turns a
  // 401 into a hard redirect to /login, which would throw a paying viewer off
  // the page mid lesson and lose their position. A raw fetch keeps that
  // decision here, where a 401 becomes a message instead of a navigation.
  // It also never calls setEpisode, so the progress guards and the mounted
  // player are left alone.
  function refreshVideoSrc() {
    const headers = {};
    const token = localStorage.getItem("sc_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(
      `${process.env.REACT_APP_API_URL}/api/courses/${courseSlug}/episodes/${episodeSlug}`,
      { headers },
    )
      .then((res) => {
        if (res.status === 401) {
          const err = new Error("Session expired.");
          err.reason = "unauthorized";
          throw err;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => data?.videoUrl || null);
  }

  function handlePlaybackFailure(info) {
    setPlaybackError(info);
  }

  function markCompleted(positionSeconds) {
    completedRef.current = true;
    if (episode?.id != null) {
      setSessionDone((d) => (d.includes(episode.id) ? d : [...d, episode.id]));
    }
    save(positionSeconds, true).then(refreshProgress);
  }

  // Bound to Plyr 'timeupdate'. The resume seek lives inside VideoPlayer.
  function onPlayerTimeUpdate(player) {
    if (!player || !player.duration) return;

    const now = Date.now();
    if (!player.paused && now - lastSaveRef.current >= SAVE_THROTTLE_MS) {
      lastSaveRef.current = now;
      save(Math.floor(player.currentTime), false);
    }

    if (!completedRef.current && player.currentTime / player.duration >= COMPLETE_AT) {
      markCompleted(Math.floor(player.currentTime));
    }
  }

  function completeAndContinue() {
    if (!episode) return;
    if (!completedRef.current) {
      const p = playerRef.current;
      const pos = p ? Math.floor(p.currentTime) : episode.durationSeconds || 0;
      markCompleted(pos);
    }
    if (episode.nextEpisodeSlug) {
      navigate(`/courses/${courseSlug}/${episode.nextEpisodeSlug}`);
    }
  }

  function goPrev() {
    if (episode?.prevEpisodeSlug) {
      navigate(`/courses/${courseSlug}/${episode.prevEpisodeSlug}`);
    }
  }

  const completedIds = new Set([
    ...(progress?.completedEpisodeIds || []),
    ...sessionDone,
  ]);
  const pct = Math.max(0, Math.min(100, Math.round(progress?.percent || 0)));
  const currentModule = course?.modules?.find((m) =>
    m.episodes.some((e) => e.slug === episodeSlug),
  );
  const locked = episode?.locked === true;
  const isExercise = episode?.kind === "exercise";

  return (
    <div className="cpl-shell">

      {/* ─── Top bar ─── */}
      <div className="cpl-top">
        <span style={{ position: "absolute", top: -50, right: 220, width: 160, height: 160, borderRadius: "50%", background: "#fff", opacity: 0.07 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 16, position: "relative", minWidth: 0 }}>
          <Link to="/my-courses" className="cpl-back" title="My courses">
            <span className="cpl-long">← My Courses</span>
            <span className="cpl-short">←</span>
          </Link>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
            <Link to="/courses" title="All courses" style={{ textDecoration: "none", minWidth: 0, overflow: "hidden" }}>
              <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                {course?.title || "Course"}
              </span>
            </Link>
            <span className="cpl-hide-sm" style={{ fontFamily: jmono, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>
              Video course
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative", flexShrink: 0 }}>
          {loggedIn && user && (
            <div className="cpl-user cpl-hide-sm">
              <span className="cpl-user-name">{firstName || "Account"}</span>
              <button className="cpl-signout" onClick={handleSignOut}>Sign out</button>
            </div>
          )}
          <button
            className="cpl-lessons"
            onClick={() => setNavOpen((o) => !o)}
            aria-expanded={navOpen}
            aria-controls="cpl-side-nav"
            aria-label={navOpen ? "Close lessons menu" : "Open lessons menu"}
          >
            ☰ Lessons
          </button>
          <button
            className="cpl-ghost"
            disabled={!episode?.prevEpisodeSlug}
            onClick={goPrev}
          >
            <span className="cpl-long">← Previous</span>
            <span className="cpl-short">←</span>
          </button>
          <button className="cpl-amber" disabled={!episode} onClick={completeAndContinue}>
            <span className="cpl-long">Complete and continue →</span>
            <span className="cpl-short">Complete →</span>
          </button>
        </div>
      </div>

      <div className="cpl-body">

        {/* ─── Sidebar ─── */}
        <aside id="cpl-side-nav" className={"cpl-side" + (navOpen ? " open" : "")}>
          <div style={{ padding: "18px 22px 16px", borderBottom: `1px solid ${C.line}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <span style={{ fontFamily: jmono, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>
                Progress
              </span>
              <span style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: C.ink }}>{pct}%</span>
            </div>
            <div style={{ height: 5, background: C.line, borderRadius: 100, overflow: "hidden" }}>
              <span style={{ display: "block", width: `${pct}%`, height: "100%", background: C.amber, transition: "width 0.5s ease" }} />
            </div>
          </div>
          <div className="cpl-side-scroll">
            {(course?.modules || []).map((mod, mi) => (
              <div key={mod.slug}>
                <div style={{ padding: "18px 22px 8px", display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, color: C.soft }}>
                    {String(mi + 1).padStart(2, "0")}
                  </span>
                  <span style={{ fontFamily: serif, fontSize: 15, fontWeight: 700, color: C.ink }}>{mod.title}</span>
                </div>
                {mod.episodes.map((ep) => {
                  const active = ep.slug === episodeSlug;
                  const done = completedIds.has(ep.id);
                  return (
                    <button
                      key={ep.slug}
                      className="cpl-row"
                      onClick={() => {
                        setNavOpen(false);
                        navigate(`/courses/${courseSlug}/${ep.slug}`);
                      }}
                      style={{
                        background: active ? C.hover : "#fff",
                        borderLeft: `3px solid ${active ? C.amber : "transparent"}`,
                      }}
                    >
                      <span
                        style={{
                          width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, color: "#fff",
                          background: done ? C.amber : "#fff",
                          border: `1.5px solid ${done ? C.amber : C.dot}`,
                        }}
                      >
                        {done ? "✓" : ""}
                      </span>
                      <span style={{ fontFamily: jmono, fontSize: 11, color: ep.kind === "exercise" ? C.red : C.muted, flexShrink: 0, width: 14, display: "flex", justifyContent: "center" }}>
                        {ep.isLocked ? <LockIcon size={12} /> : ep.kind === "exercise" ? "✎" : "▶"}
                      </span>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: active ? 600 : 400, color: C.ink, lineHeight: 1.4 }}>
                        {ep.title}
                      </span>
                      <span style={{ fontFamily: mono, fontSize: 10, color: C.soft, flexShrink: 0 }}>
                        {ep.kind === "exercise"
                          ? `${Math.round(ep.durationSeconds / 60)}m`
                          : fmtDuration(ep.durationSeconds)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            <div style={{ height: 24 }} />
          </div>
        </aside>
        {navOpen && <div className="cpl-backdrop" onClick={() => setNavOpen(false)} />}

        {/* ─── Main area ─── */}
        <div className="cpl-main" ref={mainRef}>
          <div className="cpl-content">
            {error || (!loading && !episode) ? (
              <div style={{ color: C.muted, fontSize: 15 }}>{error || "Episode not found."}</div>
            ) : !episode ? (
              <div style={{ color: C.muted, fontSize: 15 }}>Loading lesson...</div>
            ) : (
              <>
                {/* Lesson eyebrow */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", background: isExercise ? "#fef2f2" : C.blueBg, color: isExercise ? C.red : C.blueDark, padding: "4px 12px", borderRadius: 2 }}>
                    {isExercise ? "Exercise" : "Video lesson"}
                  </span>
                  {currentModule && (
                    <span style={{ fontFamily: mono, fontSize: 11, color: C.muted }}>{currentModule.title}</span>
                  )}
                </div>
                <h1 style={{ margin: "0 0 24px", fontFamily: serif, fontSize: 30, fontWeight: 700, lineHeight: 1.15, color: C.ink }}>
                  {episode.title}
                </h1>

                {/* Exercise lesson: dedicated task/why/code-flow layout */}
                {isExercise && (
                  locked ? (
                    <LockPanel
                      body="Unlock the course to open the full task and its solution."
                      onUnlock={() => navigate(`/courses/${courseSlug}`)}
                    />
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                      {(episode.exercises || []).map((ex) => (
                        <ExerciseLesson key={ex.id} exercise={ex} />
                      ))}
                    </div>
                  )
                )}

                {/* Player */}
                {!isExercise && (
                  !locked && episode.videoUrl ? (
                    <>
                      {/* The player stays mounted through a failure: unmounting
                          it would throw away the position it recovered to. */}
                      <div style={{ background: C.code, borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
                        <VideoPlayer
                          key={episode.id}
                          episodeId={episode.id}
                          src={episode.videoUrl}
                          poster={episode.thumbnailUrl}
                          captionsUrl={episode.captionsUrl}
                          resumeAt={resumeAtRef.current}
                          onTimeUpdate={onPlayerTimeUpdate}
                          playerRef={playerRef}
                          refreshSrc={refreshVideoSrc}
                          onUnrecoverableError={handlePlaybackFailure}
                        />
                      </div>
                      {playbackError && (
                        <div style={{ marginTop: 16, background: "#fef2f2", border: `1px solid ${C.red}33`, borderLeft: `3px solid ${C.red}`, borderRadius: 8, padding: "16px 20px" }}>
                          <div style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.red, marginBottom: 6 }}>
                            Playback stopped
                          </div>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 300, lineHeight: 1.7, color: C.body }}>
                            {playbackError.reason === "unauthorized" ? (
                              <>
                                Your session expired.{" "}
                                <Link to="/login" style={{ color: C.blueDark, fontWeight: 600 }}>
                                  Sign in
                                </Link>{" "}
                                to continue watching.
                              </>
                            ) : (
                              "This lesson stopped playing and could not be reloaded. Reload the page to try again."
                            )}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <LockPanel
                      body="Unlock the course to watch this lesson and reveal every exercise solution."
                      onUnlock={() => navigate(`/courses/${courseSlug}`)}
                    />
                  )
                )}

                {/* In this lesson */}
                {!isExercise && episode.summary && (
                  <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: "26px 30px", marginTop: 24 }}>
                    <div style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
                      In this lesson
                    </div>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.body }}>
                      {episode.summary}
                    </p>
                  </div>
                )}

                {/* Exercises attached to a video lesson (rare; standalone
                    exercise episodes render above instead) */}
                {!isExercise && episode.exercises && episode.exercises.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <div style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.red, marginBottom: 4 }}>
                      ✎ Exercises
                    </div>
                    {episode.exercises.map((ex) => (
                      <ExerciseBlock key={ex.id} exercise={ex} />
                    ))}
                  </div>
                )}

                {/* Bottom continue */}
                <div style={{ display: "flex", justifyContent: "center", marginTop: 36 }}>
                  <button className="cpl-blue" onClick={completeAndContinue}>
                    Complete and continue →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .cpl-shell{height:100vh;height:100dvh;display:flex;flex-direction:column;background:#fafaf9;overflow:hidden;font-family:'DM Sans',system-ui,sans-serif;}
        .cpl-top{height:56px;flex-shrink:0;background:#1e1b4b;display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:relative;overflow:hidden;gap:12px;}
        .cpl-body{flex:1;display:flex;min-height:0;}
        .cpl-side{width:340px;flex-shrink:0;background:#fff;border-right:1px solid #e4e4e7;display:flex;flex-direction:column;min-height:0;}
        .cpl-side-scroll{flex:1;overflow-y:auto;min-height:0;}
        .cpl-main{flex:1;overflow-y:auto;min-height:0;}
        .cpl-content{max-width:860px;margin:0 auto;padding:32px 40px 64px;}
        .cpl-row{width:100%;display:flex;align-items:center;gap:12px;padding:11px 22px;border:none;cursor:pointer;text-align:left;font-family:'DM Sans',sans-serif;transition:background .15s;}
        .cpl-row:hover{background:#f4f4f5 !important;}
        .cpl-ghost{font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;background:transparent;color:rgba(255,255,255,0.75);border:0.5px solid rgba(255,255,255,0.3);padding:9px 18px;border-radius:3px;cursor:pointer;transition:opacity .2s;white-space:nowrap;}
        .cpl-ghost:hover{opacity:0.8;}
        .cpl-ghost:disabled{opacity:0.35;cursor:default;}
        .cpl-amber{font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;background:#d97706;color:#fff;border:none;padding:10px 20px;border-radius:3px;cursor:pointer;transition:opacity .2s;white-space:nowrap;}
        .cpl-amber:hover{opacity:0.85;}
        .cpl-amber:disabled{opacity:0.5;cursor:default;}
        .cpl-blue{font-family:'Space Mono',monospace;font-size:12px;letter-spacing:0.04em;background:#2563eb;color:#fff;border:none;padding:14px 32px;border-radius:3px;cursor:pointer;transition:opacity .2s;}
        .cpl-blue:hover{opacity:0.85;}
        .cpl-back{display:inline-flex;align-items:center;font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;background:transparent;color:rgba(255,255,255,0.75);border:0.5px solid rgba(255,255,255,0.3);padding:9px 14px;border-radius:3px;cursor:pointer;text-decoration:none;white-space:nowrap;flex-shrink:0;transition:opacity .2s;}
        .cpl-back:hover{opacity:0.8;}
        .cpl-user{display:flex;align-items:center;gap:10px;padding-right:12px;border-right:0.5px solid rgba(255,255,255,0.2);}
        .cpl-user-name{font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:600;color:#fff;white-space:nowrap;}
        .cpl-signout{font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;background:transparent;color:rgba(255,255,255,0.55);border:none;padding:4px;cursor:pointer;transition:color .2s;}
        .cpl-signout:hover{color:rgba(255,255,255,0.9);}
        .cpl-lessons{display:none;font-family:'Space Mono',monospace;font-size:11px;letter-spacing:0.04em;background:transparent;color:rgba(255,255,255,0.75);border:0.5px solid rgba(255,255,255,0.3);padding:9px 14px;border-radius:3px;cursor:pointer;white-space:nowrap;}
        .cpl-backdrop{display:none;}
        .cpl-short{display:none;}
        @media (max-width:900px){
          .cpl-lessons{display:inline-flex;}
          .cpl-hide-sm{display:none;}
          .cpl-side{position:fixed;top:56px;left:0;bottom:0;z-index:40;transform:translateX(-100%);transition:transform .25s ease;box-shadow:0 12px 40px rgba(0,0,0,0.18);}
          .cpl-side.open{transform:translateX(0);}
          .cpl-backdrop{display:block;position:fixed;top:56px;left:0;right:0;bottom:0;background:rgba(0,0,0,0.35);z-index:30;}
          .cpl-top{padding:0 14px;}
          .cpl-ghost{padding:8px 12px;}
          .cpl-amber{padding:8px 12px;font-size:10px;}
          .cpl-content{padding:24px 18px 56px;}
        }
        @media (max-width:600px){
          .cpl-long{display:none;}
          .cpl-short{display:inline;}
        }
      `}</style>
    </div>
  );
}

export default EpisodePlayer;
